import * as THREE from 'three'
import { RenderedFaces } from '@thatopen/fragments'
import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import type { ClashResult } from '@viewer/clash/lib'
import { CLASH_COLOR_A, CLASH_COLOR_B } from './clashCores'
import { caixaDeCorteA } from '@modules/bcf/lib/bcfElementosCena'

// ---------------------------------------------------------------------------
// Realce do conflito em foco: o elemento do conjunto A de uma cor, o do
// conjunto B de outra, e o resto do modelo em fantasma.
//
// PORQUE E UM OVERLAY DO HIGHLIGHTER E NAO TINTA
//
// O viewer tem duas maneiras de mudar a cor de um elemento:
//
//   1. TINTA (`paintEngine` via `classColors`): camadas persistentes
//      (paleta por classe, Colorir por criterio, pinturas manuais da
//      Aparencia, camada do Comparar IFC / IDS). E ESTADO do utilizador.
//   2. OVERLAY do Highlighter (`select`, `hover`, `ghost`, `fade`): uma
//      camada por cima, que nao guarda nada e se tira num gesto.
//
// O realce do conflito e uma leitura MOMENTANEA de um par, por isso e um
// overlay. A consequencia que interessa: quem tinha o modelo colorido por
// criterio (ou pelo Comparar IFC, ou pelo IDS) nao perde nada. A tinta
// fica exactamente onde estava, escondida enquanto o realce dura, e volta
// intacta quando ele sai. PRECEDENCIA, portanto: realce A/B por cima de
// qualquer tinta, mas so em pixeis, nunca em estado.
//
// PORQUE E PRECISO TIRAR O PAR DA SELECCAO (visual)
//
// O Highlighter da ThatOpen trata o estilo `select` como soberano: para
// qualquer outro estilo, ele exclui os itens que estejam seleccionados
// (`getMapWithoutSelection`). Com o par seleccionado, o ambar da seleccao
// ganhava sempre e os dois elementos saiam da mesma cor, que e
// exactamente o que este realce existe para resolver. Por isso o realce
// limpa o estilo `select` (so o overlay) e repoe-o ao sair.
//
// A SELECCAO DO STORE MANTEM-SE. E dela que dependem o fantasma (o
// `viewerApi` fantasmiza tudo o que esta visivel e nao esta
// seleccionado), o botao Isolar e o painel de propriedades. Tirar o par
// da seleccao para o pintar seria partir tres coisas para arranjar uma.
//
// ISOLAR. Nao se atropelam: o Isolar mexe em VISIBILIDADE, o realce em
// COR. Isolado, o fantasma fica sem nada para fantasmizar (nao ha mais
// nada visivel) e o par continua com as suas duas cores. Ao voltar a
// "Mostrar tudo", o fantasma e reposto (`refreshGhost`).
// ---------------------------------------------------------------------------

// As cores vivem em clashCores.ts, partilhadas com o Coloring do BCF.
export { CLASH_COLOR_A, CLASH_COLOR_B }

export const CLASH_STYLE_A = 'clash-a'
export const CLASH_STYLE_B = 'clash-b'
/** O resto do modelo nas imagens dos topicos BCF: cinzento claro OPACO.
 *  O fantasma translucido (estilo `ghost`) nao sai num desenho explicito
 *  com o painel escondido: o Fragments acrescenta-o como grupo de
 *  material por cima do original e so o assenta nos frames seguintes
 *  (medido a 15/10: o material estava la, a imagem saia com as cores
 *  originais). Um estilo opaco aplica-se de imediato. */
export const CLASH_STYLE_CONTEXTO = 'clash-contexto'
export const CLASH_COLOR_CONTEXTO = '#c9ced6'

interface HighlighterLike {
  styles: { has: (k: string) => boolean; set: (k: string, v: unknown) => void }
  selection: Record<string, Record<string, Set<number>>>
  clear: (name?: string) => Promise<void>
  highlightByID: (
    name: string,
    map: Record<string, Set<number>>,
    removePrevious?: boolean,
    zoomToSelection?: boolean,
  ) => Promise<void>
}

function hl(): HighlighterLike | null {
  return viewerApi.getHighlighter() as unknown as HighlighterLike | null
}

let stylesReady = false

/** Regista os dois estilos a primeira vez que sao precisos. Feito aqui e
 *  nao no arranque do viewer para a deteccao de conflitos nao acrescentar
 *  nada ao caminho critico de quem nunca a abre. */
function ensureStyles(h: HighlighterLike): void {
  if (stylesReady && h.styles.has(CLASH_STYLE_A) && h.styles.has(CLASH_STYLE_CONTEXTO)) return
  if (!h.styles.has(CLASH_STYLE_A)) {
    h.styles.set(CLASH_STYLE_A, {
      color: new THREE.Color(CLASH_COLOR_A),
      opacity: 1,
      transparent: false,
      renderedFaces: RenderedFaces.TWO,
    })
  }
  if (!h.styles.has(CLASH_STYLE_B)) {
    h.styles.set(CLASH_STYLE_B, {
      color: new THREE.Color(CLASH_COLOR_B),
      opacity: 1,
      transparent: false,
      renderedFaces: RenderedFaces.TWO,
    })
  }
  if (!h.styles.has(CLASH_STYLE_CONTEXTO)) {
    h.styles.set(CLASH_STYLE_CONTEXTO, {
      color: new THREE.Color(CLASH_COLOR_CONTEXTO),
      opacity: 1,
      transparent: false,
      renderedFaces: RenderedFaces.TWO,
    })
  }
  stylesReady = true
}

interface HighlightState {
  /** Estado do fantasma ANTES do realce, para o repor tal e qual. */
  ghostBefore: boolean
  /** A caixa de corte foi posta por este realce (para a tirar ao sair). */
  caixa: boolean
}

let state: HighlightState | null = null

/**
 * Numero de ordem do ultimo pedido. Cada `apply` e cada `clear` levam o
 * seu; um pedido que ja nao seja o ultimo desiste em vez de escrever por
 * cima. Sem isto, desligar o realce a meio de um `apply` deixava a
 * segunda passagem do apply a repor o que o clear tinha acabado de
 * limpar, e o modelo ficava fantasma preso (visto em teste).
 */
let generation = 0

export function isClashHighlightActive(): boolean {
  return state !== null
}

/** Regista os estilos A/B no Highlighter, sem pintar nada. Para quem
 *  pinta por conta própria (as imagens dos tópicos BCF). */
export function prepararEstilosDoRealce(): boolean {
  const h = hl()
  if (!h) return false
  ensureStyles(h)
  return true
}


function mapOf(modelId: string, localId: number): Record<string, Set<number>> {
  return { [modelId]: new Set<number>([localId]) }
}

function nextFrame(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'undefined') {
      setTimeout(resolve, 16)
      return
    }
    requestAnimationFrame(() => resolve())
  })
}

/** Pinta o par. Chamado depois de a seleccao ja estar no par (o
 *  `goToClash` trata disso), porque o fantasma se calcula a partir dela.
 *
 *  A segunda passagem nao e supersticao: a sincronizacao
 *  seleccao -> estilo `select` do viewer e assincrona e dispara no mesmo
 *  gesto, por isso pode chegar DEPOIS desta. Reafirmar no frame seguinte
 *  custa nada e fecha a corrida. */
export async function applyClashHighlight(r: ClashResult): Promise<void> {
  const h = hl()
  if (!h) return
  const gen = ++generation
  ensureStyles(h)
  if (!state) {
    state = { ghostBefore: useAppStore.getState().ghostMode, caixa: false }
  }
  // Cores, fantasma e caixa ligam-se à parte (viewer/realce): o que
  // estiver desligado não se põe, e o que já estava posto sai.
  const prefs = useAppStore.getState().realce
  await viewerApi.setGhostMode(prefs.fantasma ? true : state.ghostBefore)
  if (gen !== generation) return
  if (prefs.caixa) {
    await caixaDeCorteA([
      { modelId: r.pair.modelA, localId: r.pair.localIdA },
      { modelId: r.pair.modelB, localId: r.pair.localIdB },
    ])
    if (state) state.caixa = true
  } else if (state.caixa) {
    useAppStore.getState().setSectionEnabled(false)
    state.caixa = false
  }
  if (gen !== generation) return
  if (!prefs.cores) {
    try {
      await h.clear(CLASH_STYLE_A)
      await h.clear(CLASH_STYLE_B)
    } catch (err) {
      console.debug('[clash] limpar cores ignorado', err)
    }
    await restoreSelectStyle(h)
    return
  }
  await paintPair(h, r)
  await nextFrame()
  if (gen !== generation) return
  await paintPair(h, r)
}

async function paintPair(h: HighlighterLike, r: ClashResult): Promise<void> {
  try {
    // O estilo `select` ganha a qualquer outro no Highlighter; sem o
    // limpar, os dois elementos saiam ambos de ambar.
    await h.clear('select')
    await h.highlightByID(
      CLASH_STYLE_A,
      mapOf(r.pair.modelA, r.pair.localIdA),
      true,
      false,
    )
    await h.highlightByID(
      CLASH_STYLE_B,
      mapOf(r.pair.modelB, r.pair.localIdB),
      true,
      false,
    )
  } catch (err) {
    console.debug('[clash] realce A/B ignorado', err)
  }
}

/** Tira o realce e devolve o viewer ao estado anterior: fantasma como
 *  estava e a seleccao outra vez em ambar. Idempotente. */
export async function clearClashHighlight(): Promise<void> {
  const previous = state
  state = null
  generation += 1
  const h = hl()
  if (h) {
    try {
      await h.clear(CLASH_STYLE_A)
      await h.clear(CLASH_STYLE_B)
    } catch (err) {
      console.debug('[clash] limpar realce ignorado', err)
    }
  }
  if (previous) {
    await viewerApi.setGhostMode(previous.ghostBefore)
    if (previous.caixa) useAppStore.getState().setSectionEnabled(false)
  }
  await restoreSelectStyle(h)
}

/** Repoe o ambar da seleccao a partir do store (a fonte de verdade). */
async function restoreSelectStyle(h: HighlighterLike | null): Promise<void> {
  if (!h) return
  const targets = useAppStore.getState().selectedTargets
  if (targets.length === 0) return
  const map: Record<string, Set<number>> = {}
  for (const t of targets) {
    ;(map[t.modelId] ??= new Set<number>()).add(t.localId)
  }
  try {
    await h.highlightByID('select', map, true, false)
  } catch (err) {
    console.debug('[clash] repor seleccao ignorado', err)
  }
}

/** Depois de isolar ou de mostrar tudo, o conjunto de elementos visiveis
 *  mudou e o fantasma tem de ser recalculado. Sem isto, "Mostrar tudo"
 *  com o realce ligado devolvia o modelo inteiro a cor cheia. */
export async function refreshClashHighlight(r: ClashResult | null): Promise<void> {
  if (!state) return
  const gen = ++generation
  await viewerApi.refreshGhost()
  if (gen !== generation) return
  const h = hl()
  if (h && r) await paintPair(h, r)
}

// --- quem esta a mostrar conflitos -----------------------------------------
// O painel do ribbon e a pagina do modulo partilham o mesmo estado e podem
// estar montados ao mesmo tempo (o painel vive no canvas, que nunca
// desmonta). Com um "limpo sempre que desmonto", sair da pagina apagava o
// realce que o painel ainda estava a mostrar. Mesmo registo do `idsPaint`:
// so o ultimo a sair apaga.

const surfaces = new Set<object>()

export function registerClashSurface(token: object): void {
  surfaces.add(token)
}

export async function releaseClashSurface(token: object): Promise<void> {
  surfaces.delete(token)
  if (surfaces.size === 0) await clearClashHighlight()
}

// --- diagnostico -----------------------------------------------------------
// So de leitura, no mesmo espirito do `__vn2rPaintTest` e do
// `__vn2rDebugEdges`: abre a consola e escreve `__vn2rClashHighlight()`
// para ver o que esta realmente pintado. Serve para confirmar o realce
// sem depender de olhar para os pixeis (e foi assim que se validou).
declare global {
  interface Window {
    __vn2rClashHighlight?: () => {
      active: boolean
      ghostMode: boolean
      ghostBefore: boolean | null
      selectedInStore: number
      styles: Record<string, Record<string, number[]>>
    }
  }
}

if (typeof window !== 'undefined') {
  window.__vn2rClashHighlight = () => {
    const h = hl()
    const styles: Record<string, Record<string, number[]>> = {}
    if (h) {
      for (const name of ['select', 'ghost', CLASH_STYLE_A, CLASH_STYLE_B]) {
        const map = h.selection[name]
        if (!map) continue
        const out: Record<string, number[]> = {}
        for (const [modelId, ids] of Object.entries(map)) {
          out[modelId] = [...ids].slice(0, 8)
        }
        styles[name] = out
      }
    }
    return {
      active: state !== null,
      ghostMode: useAppStore.getState().ghostMode,
      ghostBefore: state ? state.ghostBefore : null,
      selectedInStore: useAppStore.getState().selectedTargets.length,
      styles,
    }
  }
}
